// Import Dependencies
import { useState, useCallback } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";

// Local Imports
import { useIsomorphicEffect } from "app/presentation/hooks";
import { defaultTheme } from "configs/theme.config";
import i18n from "i18n/config";
import { LocaleContext } from "./context";
import { locales } from "i18n/langs";

// ----------------------------------------------------------------------

// Set the initial language from i18n or fallback to the default theme language
const initialLang =
  localStorage.getItem("i18nextLng") || defaultTheme.defaultLang;

// Ensure the initial language is supported, fallback to 'en' if not
const validInitialLang = locales[initialLang] ? initialLang : 'en';

const initialDir = i18n.dir(validInitialLang);

export function LocaleProvider({ children }) {
  const [locale, setLocale] = useState(validInitialLang);
  const [direction, setDirection] = useState(initialDir);

  // Function to update the locale dynamically
  const updateLocale = useCallback(async (newLocale) => {
    try {
      // Check if the locale is supported
      if (!locales[newLocale]) {
        console.warn(`Locale '${newLocale}' is not supported, falling back to 'en'`);
        newLocale = 'en';
      }

      // Load dayjs locale (lightweight, always lazy)
      await locales[newLocale].dayjs();
      dayjs.locale(newLocale);

      // Load flatpickr locale if available
      if (locales[newLocale].flatpickr) {
        await locales[newLocale].flatpickr();
      }

      // Change the active language (translations are already loaded via static import)
      i18n.changeLanguage(newLocale);

      // Update the state
      setLocale(newLocale);
    } catch (error) {
      console.error("Failed to update locale:", error);

      // Fallback: change language anyway
      i18n.changeLanguage(newLocale);
      setLocale(newLocale);
    }
  }, []);

  // Load the initial locale resources (dayjs/flatpickr only, translations are static)
  useIsomorphicEffect(() => {
    if (locale) {
      updateLocale(locale);
    }
  }, []);

  // Update text direction based on the current locale
  useIsomorphicEffect(() => {
    const newDir = i18n.dir(locale);
    if (newDir !== direction) {
      setDirection(newDir);
    }
  }, [locale]);

  useIsomorphicEffect(() => {
    document.documentElement.dir = direction;
    document.documentElement.lang = locale.replace('_', '-'); // Set HTML lang attribute for SEO & accessibility
  }, [direction, locale]);

  return (
    <LocaleContext
      value={{
        locale,
        updateLocale,
        direction,
        setDirection,
        isRtl: direction === "rtl",
      }}
    >
      {children}
    </LocaleContext>
  );
}

LocaleProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
